// react
import { Suspense, useEffect, useMemo, useState } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useLocation } from '@tanstack/react-router';

// material ui
import { Box, Stack } from '@mui/material';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import { useGetActivationsQuery } from '@/api/recalc/endpoints/activationsApiSlice';
import { useGetBaselineDaysByIdQuery } from '@/api/recalc/endpoints/baselineApiSlice';
import { useLazyGetDataQuery } from '@/api/recalc/endpoints/dataApiSlice';
import {
  useGetActuatorPlanByPeriodQuery,
  useGetOccupancyPredictionQuery,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import TimeRangeSelector from '@/components/ui/datetime/TimeRangeSelector';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-components
import ControlTypeDisplay from '@/features/building-optimization/components/ControlTypeDisplay';
import SynchronizedHeatingCharts from '@/features/building-optimization/components/highcharts/SynchronizedHeatingCharts';

import { DateTime } from 'luxon';

type SubscriptionUnit = {
  UnitId: number;
  DataType?: string;
  UnitTypeInputId?: number | null;
  Base?: string;
  Name?: string;
};

type PredictiveHeatControlSubscription = {
  AccountId?: number;
  JobSubscriptionId: number;
  JobType?: string;
  UserInput?: {
    ControlType?: 'pct' | 'ecl' | 'bravida' | string;
    GatewayId?: number;
    Channel?: number;
    [key: string]: unknown;
  };
  DataInput?: Record<string, SubscriptionUnit[]>;
};

type RouteLocationState = {
  subscription?: PredictiveHeatControlSubscription;
  from?: unknown;
};

type APIDataPoint = {
  Timestamp: string;
  Value: number;
};

type ActivationEntry = {
  Timestamp: string;
  Outputs?: string | null;
};

type RangeBlock = {
  x: number;
  x2: number;
  y: number;
  on: boolean;
  isBaseline?: boolean;
  isOccupancy?: boolean;
};

type SeriesMeta = {
  name: string;
  category: string;
  data: Array<[number, number]>;
  unitLabel: string;
  unitInfo: SubscriptionUnit;
  unitName: string;
  unit?: SubscriptionUnit;
};

type LoadedSeries = SeriesMeta & {
  unit: SubscriptionUnit;
};

type BaselineDay = {
  Start: string;
  End: string;
};

type OccupancyPrediction = {
  occupancy_plan?: Array<{
    interval_start: string;
    interval_end: string;
    label: boolean;
  }>;
};

type ActuatorPlan = {
  actuator_plan?: Array<{
    interval_start: string;
    interval_end: string;
    setpoint: number;
  }>;
};

function formatAndEncodeTimestampForAPI(date: Date | number): string {
  const timestamp =
    typeof date === 'number'
      ? DateTime.fromMillis(date)
      : DateTime.fromJSDate(date);
  const isoString = timestamp.toISO();
  return encodeURIComponent(isoString ?? '');
}

function formatAndEncodeTimestampForSubscriptionManagerAPI(date: Date): string {
  return DateTime.fromJSDate(date)
    .toUTC()
    .toISO({ suppressMilliseconds: true });
}

function buildActivationRangesWithOff(
  activations: ActivationEntry[] | undefined | null,
  channelKeyInput: string | number,
): RangeBlock[] {
  if (!activations || activations.length === 0) return [];

  // Normalize channel key
  let channelKey;
  if (typeof channelKeyInput === 'string') {
    channelKey =
      channelKeyInput.charAt(0).toUpperCase() + channelKeyInput.slice(1);
  } else {
    channelKey = `Output${channelKeyInput}`;
  }

  const sorted = [...activations].sort(
    (a, b) => new Date(a.Timestamp).getTime() - new Date(b.Timestamp).getTime(),
  );

  const ranges: RangeBlock[] = [];
  let lastValue = null;
  let periodStart = new Date(sorted[0].Timestamp).getTime();

  sorted.forEach((entry, i) => {
    const outputs = JSON.parse(entry.Outputs || '{}');
    const value = parseInt(outputs[channelKey], 10);
    const ts = new Date(entry.Timestamp).getTime();

    // When the value changes, close the previous period
    if (lastValue !== null && value !== lastValue) {
      ranges.push({
        x: periodStart,
        x2: ts,
        y: 0,
        on: lastValue === 100,
      });
      periodStart = ts;
    }

    lastValue = value;

    // Close at the end
    if (i === sorted.length - 1) {
      ranges.push({
        x: periodStart,
        x2: ts,
        y: 0,
        on: lastValue === 100,
      });
    }
  });

  return ranges;
}

export const Route = createFileRoute(
  '/$accountId/_admin/operations/predictive-heat-control/subscriptions/$subscriptionName/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const location = useLocation();
  const selectedSubscription = (
    location.state as RouteLocationState | undefined
  )?.subscription;

  const { data: units, isLoading: isLoadingUnits } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${selectedSubscription?.AccountId})` },
      {
        skip: !selectedSubscription?.AccountId,
      },
    );

  const [timeRange, setTimeRange] = useState<{ start: number; end: number }>({
    start: DateTime.local().minus({ days: 14 }).toUTC().toMillis(),
    end: DateTime.local().toUTC().toMillis(),
  });

  const [getData, { isLoading: isLoadingData, isFetching: isFetchingData }] =
    useLazyGetDataQuery();

  const {
    data: occupancyPrediction,
    isLoading: isLoadingOccupancyPrediction,
    isFetching: isFetchingOccupancyPrediction,
  } = useGetOccupancyPredictionQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      end: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
    },
    {
      skip: !selectedSubscription || !selectedSubscription.JobSubscriptionId,
    },
  );

  const {
    data: baselineDays,
    isLoading: isLoadingBaselineDays,
    isFetching: isFetchingBaselineDays,
  } = useGetBaselineDaysByIdQuery(
    {
      subscriptionId: selectedSubscription?.JobSubscriptionId,
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
    },
    {
      skip: !selectedSubscription || !selectedSubscription.JobSubscriptionId,
    },
  );

  const baselineRanges = (
    (baselineDays as BaselineDay[] | undefined) ?? []
  ).map((d) => ({
    x: new Date(d.Start).getTime(),
    x2: new Date(d.End).getTime(),
    y: 1,
    isBaseline: true, // extra flag to identify baseline days
  }));

  const occupancyRanges = (
    (occupancyPrediction as OccupancyPrediction | undefined)?.occupancy_plan ??
    []
  ).map((entry) => ({
    x: new Date(entry.interval_start).getTime(),
    x2: new Date(entry.interval_end).getTime(),
    y: 2,
    isOccupancy: true,
    on: entry.label === true, // store it for later use
  }));

  const {
    data: activationsECL,
    isLoading: isLoadingActivationsECL,
    isFetching: isFetchingActivationsECL,
  } = useGetActuatorPlanByPeriodQuery(
    {
      id: selectedSubscription?.JobSubscriptionId,
      start_time: formatAndEncodeTimestampForSubscriptionManagerAPI(
        new Date(timeRange.start),
      ),
      end_time: formatAndEncodeTimestampForSubscriptionManagerAPI(
        new Date(timeRange.end),
      ),
    },
    {
      skip:
        !selectedSubscription ||
        selectedSubscription.JobType !== 'PredictiveHeatControl' ||
        selectedSubscription.UserInput?.ControlType === 'pct',
    },
  );

  const {
    data: activationsPCT,
    isLoading: isLoadingActivationsData,
    isFetching: isFetchingActivationsPCT,
  } = useGetActivationsQuery(
    {
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
      gatewayId: selectedSubscription?.UserInput?.GatewayId,
    },
    {
      skip:
        !selectedSubscription ||
        selectedSubscription.JobType !== 'PredictiveHeatControl' ||
        selectedSubscription.UserInput?.ControlType === 'ecl' ||
        selectedSubscription.UserInput?.ControlType === 'bravida',
    },
  );

  const [heatingPipeTemp, setHeatingPipeTemp] = useState<SeriesMeta[]>([]);
  const [coldWaterTemp, setColdWaterTemp] = useState<SeriesMeta[]>([]);
  const [comfortTemps, setComfortTemps] = useState<SeriesMeta[]>([]);
  const [co2Series, setCo2Series] = useState<SeriesMeta[]>([]);
  const [powerConsumptionSeries, setPowerConsumptionSeries] = useState<
    SeriesMeta[]
  >([]);
  const [waterConsumptionSeries, setWaterConsumptionSeries] = useState<
    SeriesMeta[]
  >([]);
  const [relativeHumiditySeries, setRelativeHumiditySeries] = useState<
    SeriesMeta[]
  >([]);
  const [xAxisRange, setXAxisRange] = useState<{
    min: number | null;
    max: number | null;
  }>({ min: null, max: null });

  // const [activationRanges, setActivationRanges] = useState([]);
  const controlType = selectedSubscription?.UserInput?.ControlType;
  const channel = selectedSubscription?.UserInput?.Channel;

  const activationRanges = useMemo<RangeBlock[]>(() => {
    if (!selectedSubscription) return [];

    if (controlType === 'pct' && activationsPCT && channel !== undefined) {
      return buildActivationRangesWithOff(
        activationsPCT as ActivationEntry[],
        channel,
      );
    }

    const typedActivationsECL = activationsECL as ActuatorPlan | undefined;

    if (controlType === 'ecl' && typedActivationsECL?.actuator_plan?.length) {
      return typedActivationsECL.actuator_plan.map((entry) => ({
        x: new Date(entry.interval_start).getTime(),
        x2: new Date(entry.interval_end).getTime(),
        y: 0,
        on: entry.setpoint === 100,
      }));
    }

    return [];
  }, [
    selectedSubscription,
    controlType,
    channel,
    activationsPCT,
    activationsECL,
  ]);
  const xAxisRangeWithActivations = useMemo(() => {
    if (!activationRanges.length) return xAxisRange;

    const all = activationRanges.flatMap((r) => [r.x, r.x2]);

    return {
      min:
        xAxisRange.min == null
          ? Math.min(...all)
          : Math.min(xAxisRange.min, ...all),
      max:
        xAxisRange.max == null
          ? Math.max(...all)
          : Math.max(xAxisRange.max, ...all),
    };
  }, [xAxisRange, activationRanges]);

  useEffect(() => {
    if (!selectedSubscription) return;

    let cancelled = false;

    const typedUnits = units as SubscriptionUnit[] | undefined;

    const toApiArgs = (unit: SubscriptionUnit) => ({
      from: formatAndEncodeTimestampForAPI(new Date(timeRange.start)),
      to: formatAndEncodeTimestampForAPI(new Date(timeRange.end)),
      unitId: unit.UnitId,
      datatype: unit.DataType,
      unitTypeInputId: unit.UnitTypeInputId,
      aggregateType: 'Hour',
    });

    const buildTask = (
      category: string,
      unit: SubscriptionUnit,
    ): Promise<LoadedSeries | null> => {
      const unitName =
        typedUnits?.find((typedUnit) => typedUnit.UnitId === unit.UnitId)
          ?.Name || 'Unknown Unit';

      // Wrap each call so a single failure doesn't reject the whole Promise.all
      return getData(toApiArgs(unit))
        .unwrap()
        .then((fetchedData: APIDataPoint[] | undefined) => ({
          category,
          unit,
          unitName,
          unitLabel: unit.Base || '',
          data: (fetchedData || []).map((point) => [
            new Date(point.Timestamp).getTime(),
            point.Value,
          ]),
        }))
        .catch((err: unknown) => {
          console.error(
            `Failed to fetch data for unit ${unit.UnitId}: ${
              err instanceof Error ? err.message : String(err)
            }`,
          );
          return null; // swallow error for this unit
        });
    };

    const loadData = async () => {
      // 1) Build all fetch promises up front
      const tasks = (
        Object.entries(selectedSubscription.DataInput ?? {}) as Array<
          [string, SubscriptionUnit[]]
        >
      ).flatMap(([category, categoryUnits]) =>
        categoryUnits.map((unit) => buildTask(category, unit)),
      );

      // 2) Run them concurrently
      const results = await Promise.all(tasks);
      if (cancelled) return;

      // 3) Bucket series by category
      const heatingPipeTemp: SeriesMeta[] = [];
      const coldWaterTemp: SeriesMeta[] = [];
      const comfort: SeriesMeta[] = [];
      const co2: SeriesMeta[] = [];
      const powerConsumption: SeriesMeta[] = [];
      const waterConsumption: SeriesMeta[] = [];
      const relativeHumidity: SeriesMeta[] = [];

      const valid = results.filter((result): result is LoadedSeries =>
        Boolean(result),
      );

      valid.forEach((result) => {
        const seriesMeta: SeriesMeta = {
          name: result.unitName,
          category: result.category,
          data: result.data,
          unitLabel: result.unitLabel,
          unitInfo: result.unit,
          unitName: result.unitName,
        };

        if (
          result.category === 'HeatingPipeSupplyTemp' ||
          result.category === 'HeatingPipeReturnTemp'
        ) {
          heatingPipeTemp.push(seriesMeta);
        } else if (
          result.category === 'ColdWaterSupplyPipeTemp' ||
          result.category === 'ColdWaterSupplyAirTemp'
        ) {
          coldWaterTemp.push(seriesMeta);
        } else if (result.category === 'ComfortSensorTemp') {
          comfort.push(seriesMeta);
        } else if (result.category === 'CO2Level') {
          co2.push(seriesMeta);
        } else if (result.category === 'PowerConsumption') {
          powerConsumption.push(seriesMeta);
        } else if (result.category === 'WaterConsumption') {
          waterConsumption.push(seriesMeta);
        } else if (result.category === 'RelativeHumidity') {
          relativeHumidity.push(seriesMeta);
        }
      });

      setHeatingPipeTemp(heatingPipeTemp);
      setColdWaterTemp(coldWaterTemp);
      setComfortTemps(comfort);
      setCo2Series(co2);
      setPowerConsumptionSeries(powerConsumption);
      setWaterConsumptionSeries(waterConsumption);
      setRelativeHumiditySeries(relativeHumidity);

      // 4) Compute common x-axis range
      const allTimestamps = [
        ...heatingPipeTemp,
        ...coldWaterTemp,
        ...comfort,
        ...co2,
        ...powerConsumption,
        ...waterConsumption,
        ...relativeHumidity,
      ].flatMap((s) => s.data.map((p) => p[0]));

      if (allTimestamps.length > 0) {
        setXAxisRange({
          min: Math.min(...allTimestamps),
          max: Math.max(...allTimestamps),
        });
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [selectedSubscription, timeRange, units, getData]);

  if (!selectedSubscription) return null;

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <Suspense fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}>
        <GatewayLoadingIndicator
          isLoading={
            isLoadingData ||
            isFetchingData ||
            isLoadingActivationsData ||
            isFetchingActivationsPCT ||
            isLoadingActivationsECL ||
            isFetchingActivationsECL ||
            isLoadingBaselineDays ||
            isFetchingBaselineDays ||
            isLoadingOccupancyPrediction ||
            isFetchingOccupancyPrediction ||
            isLoadingUnits
          }
        />
      </Suspense>
      <Stack direction="column" spacing={2}>
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Suspense
            fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
          >
            <ControlTypeDisplay
              controlType={selectedSubscription.UserInput?.ControlType}
            />
          </Suspense>

          {/* <Box display="flex" alignItems="center"> */}
          <Suspense
            fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
          >
            <TimeRangeSelector
              timeRange={timeRange}
              setTimeRange={setTimeRange}
            />
          </Suspense>
          {/* </Box> */}
        </Box>
        {units && units.length > 0 && (
          <Suspense
            fallback={<div>{t('actions.loading', { ns: 'common' })}</div>}
          >
            <SynchronizedHeatingCharts
              heatingPipeTemp={heatingPipeTemp}
              coldWaterTemp={coldWaterTemp}
              comfortTemps={comfortTemps}
              co2Series={co2Series}
              powerConsumptionSeries={powerConsumptionSeries}
              waterConsumptionSeries={waterConsumptionSeries}
              relativeHumiditySeries={relativeHumiditySeries}
              activationRanges={activationRanges}
              baselineRanges={baselineRanges}
              occupancyRanges={occupancyRanges}
              xAxisRange={xAxisRangeWithActivations}
              loading={
                isLoadingData ||
                isFetchingData ||
                isLoadingActivationsData ||
                isFetchingActivationsPCT ||
                isLoadingActivationsECL ||
                isFetchingActivationsECL ||
                isLoadingBaselineDays ||
                isFetchingBaselineDays ||
                isLoadingOccupancyPrediction ||
                isFetchingOccupancyPrediction
              }
            />
          </Suspense>
        )}
      </Stack>
    </PageContainer>
  );
}
